<script setup lang="ts">import {useRouter as ___useRouter} from "vue-router";const $router = ___useRouter();import { $api } from "@client/lib/trpc";import { $socket } from "@client/lib/socket";import { useUserStore as ___useUserStore } from "@client/stores"; const $$user =  ___useUserStore();import { useGameStore as ___useGameStore } from "@client/stores"; const $$game =  ___useGameStore();
import LoginRegisterGroup from "@client/components/core/LoginRegisterGroup.vue";
import logo_static from "/logo-header.webp";
import logo_animated from "@client/assets/img/logo_animated.gif";
import { MenuIcon, UserIcon, LogOutIcon } from "lucide-vue-next";

type Tabs = "leaderboard" | "lobby" | "courses";

const shouldShowModal = ref(false),
	currentLogo = ref(logo_static),
	activeTab = ref<Tabs>(getCurrentTabName());
function getCurrentTabName() {
	if ($router.currentRoute.value.name === "leaderboard") return "leaderboard";
	if ($router.currentRoute.value.name === "courses") return "courses";
	return "lobby";
}
watch(
	() => $router.currentRoute.value.name,
	() => {
		if (
			$router.currentRoute.value.name !== "leaderboard" &&
			$router.currentRoute.value.name !== "courses"
		)
			activeTab.value = "lobby";
	},
);

async function handleLogout() {
	await $api.auth.logout.mutate();
	$socket.game.disconnect();
	$socket.social.disconnect();

	$$user.clearCredentials();
	$$game.players.user.info["~reset"]();
	$$game["~resetEverything"]();
	$router.push({ name: "login" });
}

async function handleTabSwitch(tabName: Tabs) {
	if (tabName === "courses") return;

	$router.push({ name: tabName });
	activeTab.value = tabName;
	$$game["~resetEverything"]();
	$socket.game.emit("userLeft");
}

function getTabClass(tabName: string) {
	return activeTab.value === tabName
		? "tw:!bg-(--accent-color-darker)  tw:!text-(--accent-color)   tw:!cursor-default"
		: undefined;
}

onUnmounted(() => (shouldShowModal.value = false));
</script>

<template>
	<v-toolbar
		class="tw:!pl-[8rem] tw:!h-16 max-sm:tw:!pl-0 tw:!pr-[4rem] tw:!w-full
			[box-shadow:1px_1px_1px_rgb(62_62_62)!important]"
		color="background tw:!overflow-visible ">
		<template #prepend>
			<img
				@click="$router.push({ path: '/' })"
				@mouseover="currentLogo = logo_animated"
				@mouseleave="currentLogo = logo_static"
				:src="currentLogo"
				loading="lazy"
				alt="mca_logo"
				width="38"
				height="32"
				:class="currentLogo === logo_animated ? 'tw:hover:scale-150' : undefined"
				class="cursor-pointer max-sm:tw:!hidden tw:filter tw:contrast-[0.1] tw:shrink-0
					tw:drop-shadow-[0px_1px_0px_rgb(0_0_0_/_60%)]" />
		</template>
		<template #default>
			<LoginRegisterGroup v-model="shouldShowModal" />
			<v-tabs
				v-model="activeTab"
				class="default-menu tw:max-[840px]:!hidden"
				color="primary"
				hide-slider>
				<v-tab
					value="lobby"
					:ripple="false"
					@click="handleTabSwitch('lobby')"
					text="Home"
					class="tw:!text-base" />
				<v-tab
					:ripple="false"
					text="Courses"
					class="tw:!text-base tw:!cursor-not-allowed" />
				<v-tab
					value="leaderboard"
					@click="handleTabSwitch('leaderboard')"
					:ripple="false"
					text="Leaderboard"
					class="tw:!text-base">
				</v-tab>
			</v-tabs>

			<div class="hamburger-menu tw:hidden tw:max-[840px]:block">
				<v-menu>
					<template #activator="{ props }">
						<v-btn
							class="tw:!flex tw:items-center tw:justify-center"
							variant="text"
							:ripple="false"
							v-bind="props">
							<MenuIcon :size="30" />
						</v-btn>
					</template>
					<v-btn-toggle
						class="md:tw:!hidden tw:flex-col tw:!h-fit tw:-mx-8 tw:mt-2 tw:!text-s">
						<v-btn
							:ripple="false"
							@click="handleTabSwitch('lobby')"
							text="Home"
							:class="getTabClass('lobby')"
							class="py-3 px-14 [text-shadow:_0px_1px_1px_black]" />
						<v-btn
							:ripple="false"
							text="Courses"
							:class="getTabClass('courses')"
							class="py-3 px-14 [text-shadow:_0px_1px_1px_black] tw:!cursor-not-allowed" />
						<v-btn
							@click="handleTabSwitch('leaderboard')"
							:ripple="false"
							text="Leaderboard"
							:class="getTabClass('leaderboard')"
							class="py-3 px-14 [text-shadow:_0px_1px_1px_black]" />
					</v-btn-toggle>
				</v-menu>
			</div>
		</template>

		<template #append>
			<button
				v-if="!$$user.isAuth"
				@click="shouldShowModal = !shouldShowModal"
				class="tw:rounded-full tw:px-10 tw:py-[0.6rem] tw:bg-black tw:font-bold tw:text-sm tw:hover:scale-[85%]
					tw:max-[840px]:hidden tw:cursor-pointer tw:duration-200 tw:hover:bg-gray-700">
				Log In
			</button>
			<v-menu v-else>
				<template #activator="{ props }">
					<div
						class="tw:mr-4 tw:flex tw:gap-4 tw:hover:bg-neutral-700 tw:px-4 tw:rounded-md tw:h-full tw:items-center
							tw:cursor-pointer"
						v-bind="props">
						<v-avatar :image="$$user.user.avatarUrl!" size="40" />
						<div class="tw:max-w-20">
							<p class="tw:text-sm tw:font-semibold tw:capitalize tw:truncate">
								{{ $$user.user.username }}
							</p>
							<p
								:title="$$user.user.email"
								class="tw:text-xs tw:font-semibold tw:text-gray-400 tw:flex tw:items-center tw:space-x-1">
								<span class="tw:truncate">
									{{ $$user.user.email }}
								</span>
							</p>
						</div>
					</div>
				</template>

				<v-list class="tw:mt-1">
					<v-list-item
						@click="$router.push({ name: 'profile' })"
						class="tw:cursor-pointer tw:hover:bg-neutral-700">
						<UserIcon class="tw:inline tw:mb-1" :size="18" />
						<span class="tw:ml-1.5">Profile</span>
					</v-list-item>
					<v-list-item
						@click="handleLogout"
						class="tw:cursor-pointer tw:hover:bg-red-900">
						<LogOutIcon class="tw:inline tw:mb-1" :size="18" />
						<span class="tw:ml-1.5">Logout</span>
					</v-list-item>
				</v-list>
			</v-menu>
		</template>
	</v-toolbar>
</template>
